import React from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import LineageFlowExplorer from "../components/LineageFlowExplorer";
import Seo from "../components/Seo";
import "../styles/lineage-article.css";

const pipelineSteps = [
  "Start with the Power BI visual, report page, semantic model, DAX measure and gold dataset behind the number.",
  "Use Microsoft Purview or Fabric lineage to identify registered assets, owners, classifications and known upstream/downstream dependencies.",
  "Parse Azure Data Factory or Synapse pipeline JSON from GitHub to identify copy activities, linked services, source datasets, sink datasets, triggers and parameters.",
  "Connect each copy activity to the bronze or staging table it writes, including load metadata, watermarks, file paths and source identifiers.",
  "Analyse stored procedures, views and scripts to extract source tables, target tables, CTEs, joins, filters, temp tables and business rules.",
  "Generate an interactive flow chart and supporting documentation that can be refreshed as metadata and code change.",
];

const purviewRows = [
  {
    item: "Asset discovery",
    purview:
      "Strong for supported source scans, catalogue search, classifications, ownership and broad metadata visibility.",
    living:
      "Adds implementation evidence from source control, ADF/Synapse JSON, SQL files, notebooks and local mapping rules.",
  },
  {
    item: "Lineage visibility",
    purview:
      "Useful where native connectors and scans expose upstream/downstream lineage.",
    living:
      "Parses hidden dependency logic in procedures, views, scripts and custom pipeline conventions.",
  },
  {
    item: "Impact analysis",
    purview:
      "Helps identify related assets across the governed catalogue.",
    living:
      "Answers engineering questions: what report breaks, which SQL object depends on this table, which pipeline loaded it and which source owns it.",
  },
];

export default function LivingLineageMapArticle() {
  return (
    <div className="site-page lineage-page blog-article-page">
      <Seo
        title="Living Data Lineage Map for Power BI, Purview, ADF and SQL | Data Apparatus"
        description="How to build a living data lineage map from Power BI visuals back through Purview, Azure Data Factory copy activities, bronze tables, SQL transformations and source systems."
        canonicalPath="/blog/living-data-lineage-map"
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Living Data Lineage Map for Power BI, Purview, ADF and SQL",
          description:
            "How to build a living data lineage map from Power BI visuals back through Purview, Azure Data Factory copy activities, bronze tables, SQL transformations and source systems.",
          author: {
            "@type": "Person",
            name: "Nurlan Alizada",
          },
          publisher: {
            "@type": "Organization",
            name: "Data Apparatus",
            url: "https://dataapparatus.com.au",
          },
          datePublished: "2026-06-27",
          mainEntityOfPage: "https://dataapparatus.com.au/blog/living-data-lineage-map",
        }}
      />

      <main className="blog-shell">
        <article className="blog-post">
          <header className="blog-header">
            <Link to="/blog" className="blog-back-link">
              Blog
            </Link>
            <p className="blog-kicker">Data lineage and impact analysis</p>
            <h1>Your Power BI dashboard is only the final layer of the story.</h1>
            <p className="blog-subtitle">
              A practical approach to tracing a number from a Power BI visual back through
              Purview, Azure Data Factory, bronze tables, SQL transformations and source
              systems.
            </p>
            <div className="blog-meta">
              <span>Nurlan Alizada</span>
              <span>Data Apparatus</span>
              <span>June 27, 2026</span>
              <span>10 min read</span>
            </div>
          </header>

          <p className="blog-lead">
            The real question behind any dashboard is simple: can you trace that number
            back to where it actually came from?
          </p>

          <p>
            In many organisations, data lineage is still a black box. The visible layer is
            a dashboard, but the actual dependency chain is scattered across Power BI
            reports, semantic models, SQL stored procedures, views, notebooks, ADF
            pipelines, bronze tables, source files, APIs and undocumented business rules.
          </p>

          <p>
            This makes simple questions surprisingly hard to answer. Where did this metric
            come from? What breaks if this source table changes? Which reports depend on
            this stored procedure? Can we trust the number? What data is our AI model
            actually using?
          </p>

          <h2>Why platform lineage alone is not enough</h2>

          <p>
            Microsoft Purview and Fabric lineage are valuable. They can help catalogue
            assets, expose native lineage where supported, show ownership and make the
            estate searchable. But in real delivery environments, critical logic often
            lives outside the catalogue view.
          </p>

          <p>
            A copy activity may be defined in Azure Data Factory JSON stored in GitHub. A
            bronze table may be populated by a parameterised pipeline. A gold dataset may
            be shaped by a stored procedure. A Power BI metric may depend on a semantic
            model measure that nobody has mapped back to the transformation rule.
          </p>

          <blockquote>
            A useful lineage map is not a static governance artefact. It is an interactive
            operating map that connects platform metadata, source-control evidence and code
            analysis.
          </blockquote>

          <h2 id="living-map">A living map of the data estate</h2>

          <p>
            The interactive flow chart below shows the chain I want teams to be able to
            follow. Select an asset to see the upstream and downstream dependencies, plus
            the evidence that should be captured.
          </p>

          <LineageFlowExplorer />

          <h2>A concrete example: ADF to bronze to SQL to Power BI</h2>

          <p>
            Imagine an Azure Data Factory pipeline is stored in GitHub. One copy activity
            reads account data from a source system and writes it into a bronze table. A
            SQL procedure then joins that bronze table with other staging assets, applies
            business rules and produces a gold dataset used by Power BI.
          </p>

          <p>
            In a conventional documentation process, someone may draw this once in a slide.
            The problem is that the slide becomes stale. A more useful approach is to
            generate the map from the artefacts that actually run the platform.
          </p>

          <ol className="blog-step-list">
            {pipelineSteps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>

          <h2>What the generated map could look like</h2>

          <p>
            The useful output is not just a picture. It should be a navigable flow chart
            where every node represents a real asset: a report visual, DAX measure, model
            table, SQL procedure, ADF dataset, copy activity, bronze table or source
            system.
          </p>

          <p>
            In practice, the tool should build this graph from the artefacts that already
            exist: Power BI metadata, Purview catalogue records, ADF/Synapse pipeline JSON,
            SQL scripts and repository conventions. The flow chart then becomes a
            practical interface for documentation, onboarding and change impact analysis.
          </p>

          <h2>Where the Python mapping tool fits</h2>

          <p>
            The notebook I am developing already points in this direction. It parses SQL
            procedures to extract procedure names, CTEs, source tables, target tables,
            joins, filters and temporary tables. That becomes one module in a broader
            lineage tool.
          </p>

          <p>
            The broader tool should eventually have separate scanners for Power BI,
            Purview, ADF/Synapse pipeline JSON, SQL scripts and internal mapping rules.
            Each scanner contributes edges to a shared lineage graph. The renderer then
            produces interactive flow charts, technical documentation and impact-analysis views.
          </p>

          <div className="blog-callout">
            <h3>The goal</h3>
            <p>
              Point the tool at a repository and governed metadata sources, then generate a
              refreshable lineage map that teams can use for change impact, migration
              planning, troubleshooting and AI context grounding.
            </p>
          </div>

          <h2>Purview plus code-level lineage</h2>

          <p>
            I would not position this as a replacement for Purview. I would position it as
            an extension. Purview is the governed catalogue and enterprise metadata layer.
            The living map fills the practical gaps that delivery teams hit when the real
            dependency is hidden in code, JSON, naming conventions or transformation rules.
          </p>

          <div className="blog-comparison">
            {purviewRows.map((row) => (
              <section key={row.item}>
                <h3>{row.item}</h3>
                <p>
                  <strong>Purview:</strong> {row.purview}
                </p>
                <p>
                  <strong>Living map:</strong> {row.living}
                </p>
              </section>
            ))}
          </div>

          <h2>Why this matters for enterprise AI</h2>

          <p>
            Enterprise AI will not only need access to data. It will need context. It needs
            to know where data came from, how it changed, which rules shaped it, who owns
            it and whether it can be trusted.
          </p>

          <p>
            That is why lineage is moving from a governance chore to a foundation for
            modern data architecture. The teams that can explain their data will be in a
            much better position to automate, migrate and apply AI safely.
          </p>

          <footer className="blog-post-footer">
            <p>
              Data Apparatus is exploring this as a practical lineage and impact-analysis
              tool for Power BI, Microsoft Purview, ADF/Synapse, SQL and Microsoft Fabric
              environments.
            </p>
            <Link to="/contact" className="primary-btn">
              Book a lineage review
            </Link>
          </footer>
        </article>
      </main>

      <Footer />
    </div>
  );
}
